import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { MapPin, Phone, MessageCircle, MessageSquareText, Truck, BadgeDollarSign, Clock, ChevronRight, ShieldCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { links } from "../lib/api";
import { usePageMeta, setJsonLd } from "../lib/seo";
import { stateBySlug, relatedStates, statePath, stateIntro, stateFaqs, cityBySlug, cityPath, cityIntro, cityFaqs } from "../lib/states";
import { Container, Reveal, EmailPill, ContactPills } from "../components/site/Reveal";
import { ProductCard } from "../components/home/ProductSections";
import { Contact } from "../components/home/InfoSections";
import LocalReviews, { RatingBadge } from "../components/home/LocalReviews";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const StatePage = () => {
  const { slug, city: citySlug } = useParams();
  const s = stateBySlug(slug);
  const city = citySlug ? cityBySlug(s, citySlug) : null;
  const { content, products, openChat } = useSite();
  const l = links(content);
  const phone = content?.phone_display || "";
  const place = city ? `${city}, ${s?.abbr}` : s?.name;
  const path = s ? (city ? cityPath(s, city) : statePath(s)) : "/";
  const faqs = useMemo(() => (!s ? [] : city ? cityFaqs(s, city, phone) : stateFaqs(s, phone)), [s, city, phone]);
  const top = useMemo(() => [...products].filter((p) => p.in_stock).sort((a, b) => b.price - a.price).slice(0, 4), [products]);

  usePageMeta({
    title: !s ? "StripWise" : city
      ? `Sell Extra Test Strips in ${city}, ${s.abbr} | Free Quote | StripWise`
      : `Sell Extra Test Strips in ${s.name} (${s.abbr}) | Free Quote | StripWise`,
    description: !s ? "" : city
      ? `Have sealed, unexpired test strips or supplies in ${city}, ${s.name}? Get a free quote from StripWise and ship free with a prepaid label. Call or text ${phone}.`
      : `Free quotes for sealed, unexpired testing supplies across ${s.name}, including ${s.cities.slice(0, 3).join(", ")}. Prepaid shipping and friendly help. Call or text ${phone}.`,
    path,
  });

  useEffect(() => {
    if (!s || (citySlug && !city)) return;
    const origin = window.location.origin;
    const crumbs = [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: "Areas We Serve", item: `${origin}/sell-test-strips` },
      { "@type": "ListItem", position: 3, name: s.name, item: `${origin}${statePath(s)}` },
    ];
    if (city) crumbs.push({ "@type": "ListItem", position: 4, name: city, item: `${origin}${cityPath(s, city)}` });
    setJsonLd("ld-faq", { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    setJsonLd("ld-state", {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Service", name: `Free quotes for sealed testing supplies in ${place}`, provider: { "@type": "LocalBusiness", name: "StripWise", telephone: content?.phone_raw, email: content?.email },
          areaServed: city ? [{ "@type": "City", name: `${city}, ${s.abbr}` }] : [{ "@type": "State", name: s.name }, ...s.cities.map((c) => ({ "@type": "City", name: `${c}, ${s.abbr}` }))] },
        { "@type": "BreadcrumbList", itemListElement: crumbs },
      ],
    });
    return () => document.getElementById("ld-state")?.remove();
  }, [s, city, citySlug, faqs, place, content?.phone_raw, content?.email]);

  if (!s) return <Navigate to="/sell-test-strips" replace />;
  if (citySlug && !city) return <Navigate to={statePath(s)} replace />;

  const otherCities = s.cities.filter((c) => c !== city);

  return (
    <>
      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#eefbf8,#ffffff)] py-14 md:py-20" data-testid="state-hero">
        <div className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-[#c9f5ec]/60 blur-3xl" />
        <Container className="relative">
          <nav className="mb-5 flex flex-wrap items-center gap-1 text-[13.5px] text-[#5b6d6a]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#0e8c7f]">Home</Link><ChevronRight size={14} />
            <Link to="/sell-test-strips" className="hover:text-[#0e8c7f]">Areas We Serve</Link><ChevronRight size={14} />
            {city ? (<><Link to={statePath(s)} className="hover:text-[#0e8c7f]">{s.name}</Link><ChevronRight size={14} /><span className="font-semibold text-[#0f2e2a]">{city}</span></>)
              : <span className="font-semibold text-[#0f2e2a]">{s.name}</span>}
          </nav>
          <div className="max-w-[820px] sw-fade-up">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#dcefea] bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-[#0b6f65]"><MapPin size={14} />{city ? `Serving ${city} & all of ${s.name}` : `Serving all of ${s.name} (${s.abbr})`}</span>
              <RatingBadge stateName={s.name} />
            </div>
            <h1 className="mt-5 font-jakarta text-[36px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#0f2e2a] md:text-[54px]" data-testid="state-title">
              Sell Your Extra Test Strips in <span className="text-[#0e8c7f]">{place}</span>
            </h1>
            <p className="mt-5 text-[17.5px] leading-relaxed text-[#4b5d5a]">{city ? cityIntro(s, city) : stateIntro(s)}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => openChat(`Hi! I'm in ${place} and I'd like to sell my diabetic supplies.`)} className="sw-btn-primary" data-testid="state-chat-btn"><MessageCircle size={16} />Get My {city || s.abbr} Free Quote</button>
              <a href={l.tel} className="sw-btn-outline"><Phone size={16} />Call / Text {phone}</a>
              <a href={l.wa} target="_blank" rel="noreferrer" className="sw-btn-outline"><MessageSquareText size={16} />WhatsApp</a>
            </div>
            <div><EmailPill className="mt-5" /></div>
          </div>
        </Container>
      </section>

      <section className="py-14">
        <Container className="grid gap-5 md:grid-cols-4">
          {[[Truck, city ? "Free shipping label" : "Free shipping label", `Drop your box off at a post office in ${city || s.cities[0]} with the prepaid label we send.`], [Clock, "Clear updates", "We keep you posted at every step."], [BadgeDollarSign, "Fair offers", "Clear, no-obligation quotes."], [ShieldCheck, "Real people", "A real team member answers your call or text."]].map(([I, t, d], i) => (
            <Reveal key={t} delay={i * 80} className="sw-card p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#d3f6ee] text-[#0e8c7f]"><I size={19} /></span>
              <h2 className="mt-4 text-[17px] font-bold text-[#0f2e2a]">{t}</h2>
              <p className="mt-1.5 text-[14.5px] leading-relaxed text-[#566865]">{d}</p>
            </Reveal>
          ))}
        </Container>
      </section>

      <section className="bg-[#f4fbf9] py-16">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <Reveal>
              <div className="sw-section-eyebrow">{city ? `More ${s.name} cities we buy from` : `Cities we buy from in ${s.name}`}</div>
              <h2 className="sw-h2 mt-3">{city ? `Free quotes in ${city} & nearby` : `Free quotes in ${s.cities.slice(0, 3).join(", ")} & more`}</h2>
              <p className="mt-4 text-[16px] leading-[1.75] text-[#4b5d5a]">
                We accept sealed, unexpired items such as Dexcom sensors, FreeStyle Libre sensors, Omnipod pods, and OneTouch, Contour and Accu-Chek test strips from people in {city ? `${city} and across ${s.name}` : `every corner of ${s.name}`}. Wherever you are in the USA, the process is the same: free quote, prepaid label, and payment your way.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2" data-testid="state-cities">
                {otherCities.map((c) => (
                  <li key={c}>
                    <Link to={cityPath(s, c)} onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 rounded-full border border-[#d6efe9] bg-white px-3.5 py-2 text-[14px] font-semibold text-[#0b6f65] hover:border-[#0e8c7f] hover:bg-[#effaf7] transition-colors" data-testid="city-link">
                      <MapPin size={13} />Sell test strips in {c}, {s.abbr}
                    </Link>
                  </li>
                ))}
                {city && (
                  <li><Link to={statePath(s)} onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 rounded-full bg-[#0e8c7f] px-3.5 py-2 text-[14px] font-semibold text-white hover:bg-[#0b7569]">All of {s.name}</Link></li>
                )}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="grid gap-4 sm:grid-cols-2">
                {top.map((p) => <ProductCard key={p.id} p={p} onChat={(x) => openChat(`Hi! I'm in ${place}. Quote for ${x.name} (${x.size})?`)} />)}
              </div>
              <Link to="/prices" className="mt-4 inline-flex items-center gap-1 text-[14.5px] font-semibold text-[#0e8c7f] hover:underline">See the full price list <ChevronRight size={15} /></Link>
            </Reveal>
          </div>
        </Container>
      </section>

      <LocalReviews stateName={s.name} place={city || s.name} />

      <section className="bg-[#f4fbf9] py-16">
        <Container className="max-w-[900px] lg:w-[70%]">
          <h2 className="sw-h2 text-center">{city || s.name} seller FAQs</h2>
          <ContactPills className="mt-4" />
          <Accordion type="single" collapsible className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`s${i}`} className="rounded-2xl border border-[#e3f0ed] bg-white px-6">
                <AccordionTrigger className="py-5 text-left font-jakarta text-[16.5px] font-semibold text-[#0f2e2a] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-5 text-[15.5px] leading-relaxed text-[#4b5d5a]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10">
            <h3 className="text-center text-[16px] font-bold text-[#0f2e2a]">We also buy in nearby states</h3>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {relatedStates(s).map((r) => (
                <Link key={r.slug} to={statePath(r)} onClick={() => window.scrollTo(0, 0)} className="sw-chip" data-testid="related-state-link">{r.name}</Link>
              ))}
              <Link to="/sell-test-strips" className="sw-chip">All 50 states</Link>
            </div>
          </div>
        </Container>
      </section>
      <Contact />
    </>
  );
};

export default StatePage;
